import { find, size } from "lodash";
import moment from "moment";

import { AddressValidation, BulkyItem, BulkyItemsCategory, DayOfWeekSetting } from "src/interfaces";
import { BULK_ITEM_CATEGORY_OTHER_ID, DAY_PERIOD_TYPE_ID, DEFAULT_TIME } from "src/constants";
import { TimeRangeList } from "src/interfaces/citySettings";

export const getItemSettings = (
  item: BulkyItem,
  date: Date | string,
  categories: BulkyItemsCategory[],
  addressValidation?: AddressValidation
) => {
  const currentDate = moment(date);
  const currentDateDay = currentDate.day();

  const categorySetting = find(categories, {
    bulkyCategoryTypeId: item.bulkyCategoryTypeId,
  });

  const itemSetting =
    item.bulkyCategoryTypeId === BULK_ITEM_CATEGORY_OTHER_ID
      ? find(categorySetting?.items, {
          id: item.bulkyItemTypeId,
        })
      : find(categorySetting?.items, {
          bulkyItemTypeId: item.bulkyItemTypeId,
        });

  if (!categorySetting)
    return {
      settingsMessage: "",
      price: 0,
    };

  // price logic
  let price = categorySetting?.price;

  if (itemSetting && (itemSetting.price || itemSetting.price === 0)) {
    if (itemSetting.startFrom && itemSetting.endDate) {
      const startDate = moment(itemSetting?.startFrom).startOf("day");
      const endDate = moment(itemSetting?.endDate).endOf("day");

      if (currentDate.isBetween(startDate, endDate, "day", "[]")) {
        price = itemSetting.price;
      }
    } else if (itemSetting.startFrom) {
      const startDate = moment(itemSetting?.startFrom).startOf("day");

      if (currentDate.isAfter(startDate)) {
        price = itemSetting.price;
      }
    } else if (itemSetting.endDate) {
      const endDate = moment(itemSetting?.endDate).endOf("day");

      if (currentDate.isBefore(endDate)) {
        price = itemSetting.price;
      }
    } else {
      price = itemSetting.price;
    }
  } else if (item.price || item.price === 0) {
    price = item.price;
  }

  let pickupWindowStartTime: string | undefined = DEFAULT_TIME;
  let pickupWindowEndTime: string | undefined = DEFAULT_TIME;
  let maxPickups = undefined;

  // customs settings logic for limits by day of week
  if (!!size(categorySetting.dayOfWeekList)) {
    const categoriesWithZoneId = categorySetting?.dayOfWeekList?.filter((dayOfWeek) =>
      addressValidation?.zoneIds?.some(
        (zoneId) => dayOfWeek?.zoneIds?.includes(zoneId) && dayOfWeek.dayOfWeek === currentDateDay
      )
    );

    const categoriesWithZoneIdFiltered = Object.values(
      categoriesWithZoneId?.reduce((acc, curr) => {
        if (!acc[curr.dayOfWeek] || curr.maxPickups > acc[curr.dayOfWeek].maxPickups) {
          acc[curr.dayOfWeek] = curr;
        }
        return acc;
      }, {} as DayOfWeekSetting as any)
    ) as DayOfWeekSetting[];

    const categoriesWithoutZoneId = categorySetting?.dayOfWeekList?.filter(
      (dayOfWeek: DayOfWeekSetting) =>
        !dayOfWeek?.zoneIds?.length && dayOfWeek.dayOfWeek === currentDateDay
    );

    const currentCategory = !!categoriesWithZoneIdFiltered?.length
      ? categoriesWithZoneIdFiltered[0]
      : categoriesWithoutZoneId[0];

    if (currentCategory && currentCategory.isEnabled) {
      pickupWindowStartTime = currentCategory.pickupWindowFromTime;
      pickupWindowEndTime = currentCategory.pickupWindowToTime;
      maxPickups = currentCategory.maxPickups;
    }
  } else if (categorySetting.allDayOfWeek && categorySetting.allDayOfWeek.isEnabled) {
    const startDate = moment(categorySetting.allDayOfWeek.effectiveFrom).startOf("day");
    const endDate = moment(categorySetting.allDayOfWeek.effectiveTo).endOf("day");

    if (currentDate.isBetween(startDate, endDate, "day", "[]")) {
      pickupWindowStartTime = categorySetting.allDayOfWeek.pickupWindowFromTime;
      pickupWindowEndTime = categorySetting.allDayOfWeek.pickupWindowToTime;
      maxPickups = categorySetting.allDayOfWeek.maxPickups;
    }
  }

  // customs settings logic for limits by time range
  if (!!size(categorySetting.timeRangeList)) {
    const categoriesWithZoneId = categorySetting?.timeRangeList?.filter((timeRange) =>
      addressValidation?.zoneIds?.some(
        (zoneId) =>
          timeRange?.zoneIds?.includes(zoneId) && timeRange.periodTypeId === DAY_PERIOD_TYPE_ID
      )
    );

    const categoriesWithZoneIdFiltered = Object.values(
      categoriesWithZoneId?.reduce((acc, curr) => {
        if (
          !acc[curr.periodTypeId as number] ||
          curr.maxPickups > acc[curr.periodTypeId as number].maxPickups
        ) {
          acc[curr.periodTypeId as number] = curr;
        }
        return acc;
      }, {} as TimeRangeList as any)
    ) as TimeRangeList[];

    const categoriesWithoutZoneId = categorySetting?.timeRangeList?.filter(
      (timeRange: TimeRangeList) =>
        !timeRange?.zoneIds?.length && timeRange.periodTypeId === DAY_PERIOD_TYPE_ID
    );

    const currentCategory = !!categoriesWithZoneIdFiltered?.length
      ? categoriesWithZoneIdFiltered[0]
      : categoriesWithoutZoneId[0];

    if (!!currentCategory) {
      maxPickups = currentCategory.maxPickups || 0;
      pickupWindowStartTime = currentCategory?.pickupWindowFromTime;
      pickupWindowEndTime = currentCategory?.pickupWindowToTime;
    }

    const categoriesWithZoneIdExceptDay = categorySetting?.timeRangeList?.filter((timeRange) =>
      addressValidation?.zoneIds?.some(
        (zoneId) =>
          timeRange?.zoneIds?.includes(zoneId) && timeRange.periodTypeId !== DAY_PERIOD_TYPE_ID
      )
    );

    const categoriesWithZoneIdFilteredExceptDay = Object.values(
      categoriesWithZoneIdExceptDay?.reduce((acc, curr) => {
        if (
          !acc[curr.periodTypeId as number] ||
          curr.maxPickups > acc[curr.periodTypeId as number].maxPickups
        ) {
          acc[curr.periodTypeId as number] = curr;
        }
        return acc;
      }, {} as TimeRangeList as any)
    ) as TimeRangeList[];

    const categoriesWithoutZoneIdExceptDay = categorySetting?.timeRangeList?.filter(
      (timeRange: TimeRangeList) =>
        !timeRange?.zoneIds?.length && timeRange.periodTypeId !== DAY_PERIOD_TYPE_ID
    );

    const currentCategoryExceptDay = !!categoriesWithZoneIdFilteredExceptDay?.length
      ? categoriesWithZoneIdFilteredExceptDay[0]
      : categoriesWithoutZoneIdExceptDay[0];

    if (!!currentCategoryExceptDay) {
      maxPickups = currentCategoryExceptDay.maxPickups || 0;
      pickupWindowStartTime = currentCategoryExceptDay?.pickupWindowFromTime;
      pickupWindowEndTime = currentCategoryExceptDay?.pickupWindowToTime;
    }
  }

  const settingsMessage = itemSetting?.message || categorySetting?.message || "";

  return {
    settingsMessage,
    price,
    pickupWindowStartTime: pickupWindowStartTime || DEFAULT_TIME,
    pickupWindowEndTime: pickupWindowEndTime || DEFAULT_TIME,
    maxPickups,
  };
};
